import React from "react"
import moment from "moment"
import { Flex, Select, Skeleton } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { MONTH_LABEL_FORMAT } from "./constants"
import { useAvailabilityCalendar } from "./hooks/useAvailabilityCalendar"
import AvailabilityDayCard from "./components/AvailabilityDayCard"
import AvailabilityLegend from "./components/AvailabilityLegend"
import styles from "./index.module.css"

const WEEKDAY_LABELS = moment.weekdaysShort()

/**
 * Availability Calendar — "can I sell this day, and if not, why", for one listing at a time.
 *
 * Sibling of the booking calendar rather than a mode of it: the two answer different questions and
 * share no filters. The listing is required, so the page opens on an empty state.
 *
 * The backing endpoint is not built yet; see docs/availability-calendar-api.md.
 */
const AvailabilityCalendar = () => {
  const { t } = useTranslation()
  const {
    listingId,
    setListingId,
    listingOptions,
    isLoadingListings,
    monthDate,
    stepMonth,
    weeks,
    days,
    listing,
    isLoading,
    hasFailed
  } = useAvailabilityCalendar()

  // Day details open in a drawer, which arrives with the endpoint — see the contract's checklist.
  const handleOpenDetails = () => {}

  const renderGrid = () => {
    if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} title={false} className={styles.skeleton} />

    if (hasFailed) {
      return (
        <Flex vertical align="center" justify="center" gap="var(--spacing-s)" className={styles.placeholder}>
          <span className={styles.placeholderIcon}>
            <Icon name="AlertTriangle" width={28} height={28} />
          </span>
          <Text variant="caption" size="lg" className={styles.placeholderText}>
            {t("availability.load_failed", { defaultValue: "Availability could not be loaded for this listing" })}
          </Text>
        </Flex>
      )
    }

    return (
      <div className={styles.grid} role="grid">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className={styles.weekdayCell} role="columnheader">
            {label}
          </div>
        ))}
        {weeks.map((week) =>
          week.map(({ dateKey, isCurrentMonth }) => (
            <AvailabilityDayCard
              key={dateKey}
              dateKey={dateKey}
              isCurrentMonth={isCurrentMonth}
              day={days[dateKey] || null}
              listing={listing}
              onOpenDetails={handleOpenDetails}
            />
          ))
        )}
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <Flex align="center" gap="var(--spacing-m)" className={styles.listingRow}>
        <Text variant="sub" size="md" fw="500" className={styles.listingLabel}>
          {t("availability.listing", { defaultValue: "Listing" })}
        </Text>
        <Select
          showSearch
          optionFilterProp="label"
          className={styles.listingSelect}
          placeholder={t("availability.select_listing", { defaultValue: "Select" })}
          value={listingId}
          options={listingOptions}
          loading={isLoadingListings}
          onChange={setListingId}
        />
      </Flex>

      {!listingId ? (
        <Flex vertical align="center" justify="center" gap="var(--spacing-s)" className={styles.placeholder}>
          <span className={styles.placeholderIcon}>
            <Icon name="calendar" width={28} height={28} />
          </span>
          <Text variant="caption" size="lg" className={styles.placeholderText}>
            {t("availability.empty", { defaultValue: "Select listing to view availability calendar" })}
          </Text>
        </Flex>
      ) : (
        <>
          <Flex align="center" justify="space-between" gap="var(--spacing-m)" wrap className={styles.monthRow}>
            <Flex align="center" gap="var(--spacing-xs)">
              <button
                type="button"
                className={styles.stepButton}
                aria-label={t("availability.previous_month", { defaultValue: "Previous month" })}
                onClick={() => stepMonth(-1)}
              >
                <Icon name="ChevronLeft" width={20} height={20} />
              </button>
              <Text variant="sub" size="lg" fw="500" className={styles.monthLabel}>
                {monthDate.format(MONTH_LABEL_FORMAT)}
              </Text>
              <button
                type="button"
                className={styles.stepButton}
                aria-label={t("availability.next_month", { defaultValue: "Next month" })}
                onClick={() => stepMonth(1)}
              >
                <Icon name="ChevronRight" width={20} height={20} />
              </button>
            </Flex>
            <AvailabilityLegend />
          </Flex>

          {renderGrid()}
        </>
      )}
    </div>
  )
}

export default AvailabilityCalendar
